import { X } from '@squareup/dex-icons/market/action';
import { ApiExplorerInputType } from '@squareup/dex-types-shared-api-explorer';
import { ComboboxActionButton } from '@squareup/dex-ui';
import { Box } from '@squareup/dex-ui-shared-base';
import { commonIconStyles } from '@squareup/dex-ui-shared-icon-styles';
import { getDefaultForItemInProperty } from '@squareup/dex-utils-oas';
import {
  getDateTimeFormat,
  getFieldTypeForItemProperty,
} from '@squareup/dex-utils-shared-api-explorer';
import React, { FC, useCallback, ComponentProps, memo } from 'react';

import { ApiExplorerCollapsibleFieldLabel } from './ApiExplorerCollapsibleFieldLabel';
import { ApiExplorerEnumField } from './ApiExplorerEnumField';
import { ApiExplorerObjectField } from './ApiExplorerObjectField';
import { ApiExplorerPrimitiveField } from './ApiExplorerPrimitiveField';
import { ApiExplorerFieldAddButton } from './helpers/ApiExplorerFieldAddButton';
import { ApiExplorerFieldAddButtonSpecialValuesDropdown } from './helpers/ApiExplorerFieldAddButtonSpecialValuesDropdown';
import { ApiExplorerFieldProps } from './props';
import { useApiExplorerFieldContext } from './providers/ApiExplorerFieldProvider';

const strings = {
  add: 'Add to array',
};

interface ItemProps extends ApiExplorerFieldProps {
  index: number;
  onRemove: (index: number) => void;
}

const ApiExplorerArrayItem: FC<ItemProps> = memo(
  ({ currentValue, index, property, path, depth = 0, onChange, onRemove }) => {
    // We have to call the appropriate APIExplorerField components manually based on the type. Some restrictions
    // to our Array fields because of this are:
    // 1. Only allow primitives and enums
    // 2. Only allow objects
    // We will not allow arrays of maps, arrays of arrays, or arrays of any/schema types
    const newName = `${property.name}[${index}]`;
    const newPath = `${path}/${index}`;

    const onRemoveItem = useCallback<
      ComponentProps<typeof ComboboxActionButton>['onClick']
    >(() => {
      onRemove(index);
    }, [index, onRemove]);

    const onItemChanged = useCallback<typeof onChange>(
      (value, path, op) => {
        if (!property.itemEntity && (value === undefined || op === 'remove')) {
          // If the value is a primitive, and the user clears it, we will default to the default primitive for that data type
          onChange(
            getDefaultForItemInProperty(property) as ApiExplorerInputType,
            path
          );
        } else {
          onChange(value, path, op);
        }
      },
      [onChange, property]
    );

    const removeButton = (
      <ComboboxActionButton onClick={onRemoveItem}>
        <X
          width={16}
          height={16}
          viewBox="0 0 24 24"
          className={commonIconStyles['icon-color-20']}
        />
      </ComboboxActionButton>
    );

    const itemFieldType = getFieldTypeForItemProperty(property);
    function getField() {
      switch (itemFieldType) {
        case 'enum':
          return (
            <ApiExplorerEnumField
              // This check does already happen in `getFieldTypeForItemProperty` but Typescript
              // has no available guard for this
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              entity={property.itemEntity!}
              name={newName}
              path={newPath}
              currentValue={currentValue}
              onChange={onItemChanged}
              actionButton={removeButton}
              actionOnClear="empty-string"
            />
          );
        case 'object':
          return (
            <ApiExplorerCollapsibleFieldLabel
              name={newName}
              currentValue={currentValue}
              path={newPath}
              depth={depth + 1}
              onChange={onItemChanged}
            >
              <ApiExplorerObjectField
                depth={depth + 1}
                // This check does already happen in `getFieldTypeForItemProperty` but Typescript
                // has no available guard for this
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                entity={property.itemEntity!}
                currentValue={currentValue}
                onChange={onItemChanged}
                path={newPath}
              />
            </ApiExplorerCollapsibleFieldLabel>
          );
        case 'primitive':
        default:
          return (
            <ApiExplorerPrimitiveField
              currentValue={currentValue}
              path={newPath}
              name={newName}
              type={property.itemType}
              datetimeFormat={getDateTimeFormat(property)}
              depth={depth}
              onChange={onItemChanged}
              actionButton={removeButton}
              // Since arrays must default to some default value for items, that means
              // strings always start off empty. Therefore, we don't need to show it in the dropdown,
              // or show the special filled state for it
              isEmptyStringAllowed={false}
            />
          );
      }
    }

    return (
      <Box
        margin={{ bottom: '1x' }}
        key={newName}
        testId={`array-field-${newPath}`}
      >
        {getField()}
      </Box>
    );
  }
);

ApiExplorerArrayItem.displayName = 'ApiExplorerArrayItem';

interface Props extends ApiExplorerFieldProps {}

const ApiExplorerArrayField: FC<Props> = memo(
  ({ currentValue, depth, property, onChange, path }) => {
    const { isNullable } = useApiExplorerFieldContext();

    const arrayValues = Array.isArray(currentValue) ? currentValue : [];

    const onArrayAdded = useCallback(() => {
      // Rules
      // 1. If the item value is a primitive, we will prefill with an empty string
      // 2. If the item value is an object, add an empty object to the array
      const newValue = getDefaultForItemInProperty(
        property
      ) as ApiExplorerInputType;
      if (!currentValue) {
        onChange([newValue], path);
      } else {
        onChange(newValue, `${path}/-`);
      }
    }, [currentValue, onChange, path, property]);

    const onArrayRemoved = useCallback(
      (index: number) => {
        // If there's only one left, remove the whole array
        if (arrayValues.length === 1) {
          onChange(undefined, path, 'remove');
        } else {
          onChange(undefined, `${path}/${index}`, 'remove');
        }
      },
      [arrayValues.length, onChange, path]
    );

    const addButton = (
      <ApiExplorerFieldAddButton
        onClick={onArrayAdded}
        trackingId={`api-explorer-array-field-add-${property.id}`}
        extraValuesTrigger={
          isNullable ? (
            <ApiExplorerFieldAddButtonSpecialValuesDropdown
              trackingId={`api-explorer-array-field-add-extra-values-${property.id}`}
              path={path}
              onChange={onChange}
              currentValue={currentValue}
            />
          ) : null
        }
      >
        {strings.add}
      </ApiExplorerFieldAddButton>
    );

    const arrayFields = arrayValues.map((item, index) => {
      return (
        <ApiExplorerArrayItem
          key={`${property.name}/${index}`}
          currentValue={item}
          index={index}
          property={property}
          path={path}
          depth={depth}
          onChange={onChange}
          onRemove={onArrayRemoved}
        />
      );
    });

    return (
      <>
        {arrayValues.length > 0 && arrayFields}
        {addButton}
      </>
    );
  }
);

ApiExplorerArrayField.displayName = 'ApiExplorerArrayField';

export { ApiExplorerArrayField };
